import { Div, Flex, Spacer, Text } from '@sendoutcards/quantum-design-ui'
import { Icon } from 'src/design_system/atoms/icons/components/Icon'
import { useMemo, useSelector } from 'src/hooks'
import { useCheckout, useProductVariants } from 'src/react_query'
import useHandleProductActions from 'src/pricing_page/hooks/useHandleProductActions'

// This component relies heavily on Saleor variant attributes. Setting suggested product/promotion
// details in saleor will dynamically change out the UI and control promotions applied to the cart
export const SuggestedPlanAddon = () => {
  const account = useSelector(state => state.user.account)
  const accountType = account ? account.type : 'NON_AUTH'

  const { data } = useCheckout()

  const { addToCart, checkIsInCart, isUpdatingCheckout } =
    useHandleProductActions()

  const suggestedAddons = data?.lines.map(line => ({
    addon: line.variant.parsedAttributes.suggestedAddon,
    promo: line.variant.parsedAttributes.suggestedAddonPromo,
  }))

  const forUserTypes =
    data?.lines
      .flatMap(
        line => line.variant.parsedAttributes.suggestedAddonPromo.forUserTypes,
      )
      .filter((t): t is string => Boolean(t)) ?? []
  const addonIds = suggestedAddons
    ?.map(a => a.addon?.reference)
    .filter((r): r is string => Boolean(r))

  const { data: addons } = useProductVariants(
    {
      ids: addonIds ?? [],
      first: 50,
    },
    addonIds && addonIds.length > 0,
  )

  const nonSelectedAddons = useMemo(
    () =>
      addons?.pages.flatMap(page =>
        page.edges.filter(edge => !checkIsInCart(data, edge.node.id)),
      ),
    [addons, checkIsInCart, data],
  )

  const textColor = 'primaryHeading'

  // Promo codes/vouchers from suggested products are applied via useUpdateCheckoutVouchers
  const handleSelectAddon = (variantId: string) => {
    if (isUpdatingCheckout) return
    addToCart(variantId)
  }

  // Saleor variant attribute for promo user types sends a list of user types this
  // promo is targeting
  const hasPromo = forUserTypes.includes(accountType)
  if (!nonSelectedAddons || nonSelectedAddons.length === 0) {
    return null
  }

  return (
    <Flex width="100%" flexWrap="wrap">
      <Text
        content="Suggested Addons"
        type="body"
        weight="bold"
        color="primaryHeading"
      />
      <Spacer space="x1" />
      {nonSelectedAddons.map(edge => {
        const promo = suggestedAddons?.find(
          add => add.addon.reference === edge.node.id,
        )?.promo
        const shouldApplyDiscount = !!promo && hasPromo
        const discountedAmount =
          promo?.amount && edge.node.pricing?.price?.gross.amount
            ? edge.node.pricing?.price?.gross.amount - promo.amount
            : 0

        return (
          <Flex
            key={edge.node.id}
            width="100%"
            justifyContent="space-between"
            alignItems="center"
            style={{
              background: 'white',
              boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.04)',
              borderTop: '',
            }}
            inset="x1_5"
            outset={{ bottom: 'x1' }}
            borderRadius="medium"
            onClick={() => handleSelectAddon(edge.node.id)}
            cursor="pointer"
          >
            <Flex alignItems="center">
              <Div
                minWidth="3px"
                height="35px"
                style={{
                  background:
                    'linear-gradient(161deg, rgb(196, 113, 245), rgb(43 215 251) 100%)',
                  borderRadius: '2px',
                }}
                outset={{ right: 'x1_5' }}
              />
              <Flex
                id="check-circle"
                minWidth="16px"
                borderColor="background"
                borderStyle="solid"
                borderWidth="bold"
                style={{ border: '2px solid #D4D4D8' }}
                minHeight="16px"
                borderRadius="circle"
                inset="2px"
                justifyContent="center"
                alignItems="center"
                backgroundColor="transparent"
                outset={{ right: '12px' }}
              ></Flex>
              <Flex flexDirection="row" alignItems="center" columnGap="x_5">
                <Icon
                  name="promptings_logo"
                  size={32}
                  color={'#FFF'}
                  gradientTransform="rotate(90)"
                  gradient={[
                    { offset: '-56%', color: '#277AD4' },
                    { offset: '58.3%', color: '#4AB5D9' },
                    { offset: '152%', color: '#67E5DD' },
                  ]}
                />
                <Text
                  content={edge.node.name}
                  type={'caption'}
                  weight="semiBold"
                  color={textColor}
                />
              </Flex>
            </Flex>
            <Flex
              style={{
                marginLeft: 'auto',
                boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.04)',
              }}
              alignItems="flex-end"
              justifyContent="center"
            >
              <Flex
                flexDirection="column"
                justifyContent="center"
                alignItems="center"
              >
                <Flex>
                  <Text
                    content={`$${edge.node.pricing?.price?.gross.amount}`}
                    type={shouldApplyDiscount ? 'body' : 'largeBody'}
                    color={textColor}
                    weight="bold"
                    style={
                      shouldApplyDiscount
                        ? {
                            textDecoration: 'line-through',
                            color: 'red',
                            fontSize: '13px',
                            lineHeight: 1.5,
                          }
                        : { lineHeight: 1.1 }
                    }
                  />
                </Flex>
                {shouldApplyDiscount && (
                  <Flex>
                    <Text
                      content={`$${discountedAmount}`}
                      type={'largeBody'}
                      color={textColor}
                      weight="bold"
                      outset={{ right: 'x_25' }}
                    />
                    <Text
                      color={textColor}
                      type="footnote"
                      style={{ lineHeight: 2.3 }}
                      content={promo?.content ?? ''}
                    />
                  </Flex>
                )}
              </Flex>
            </Flex>
          </Flex>
        )
      })}
    </Flex>
  )
}
